<script lang="ts">
import { defineComponent } from "vue";
import { apiService } from "@/services/services";
import LoadingOverlay from "@/shared/components/LoadingOverlay.vue";
import KlereoCaptcha from "@/shared/components/KlereoCaptcha.vue";
import {
  hasUppercase,
  hasLowercase,
  hasNumber,
  hasSpecialChar,
} from "@/shared/utils/validation-rules";
import sha1 from "sha1";

export default defineComponent({
  components: { KlereoCaptcha, LoadingOverlay },
  data() {
    return {
      saving: false,
      error: null as any,
      promise: undefined as Promise<unknown> | undefined,
      hasUppercase,
      hasLowercase,
      hasNumber,
      hasSpecialChar,
      complete: false,
      refreshCaptcha: 0,
    };
  },
  methods: {
    async onSubmit(
      {
        oldPass,
        newPass,
        captcha,
      }: {
        oldPass: string;
        newPass: string;
        captcha: string;
      },
      node: any
    ) {
      this.saving = true;
      this.error = null;
      try {
        await apiService.changePass({
          oldPass: sha1(oldPass),
          newPass,
          captcha,
        });
        this.complete = true;
        this.$formkit.reset("changePassword");
      } catch (e) {
        this.error = e;
        switch ((e as { message: string }).message) {
          case "Captcha":
            node.setErrors({ captcha: this.$t("Captcha invalide") });
            break;
          case "Identification incorrecte":
            node.setErrors({ oldPass: this.$t("Mot de pase incorrect") });
            break;
          default:
            node.setErrors(["" + e]);
        }
      } finally {
        this.refreshCaptcha++;
        this.saving = false;
      }
    },
  },
});
</script>

<template>
  <div>
    <loading-overlay :loading="saving" :error="error" />
    <FormKit
      id="changePassword"
      type="form"
      @submit="onSubmit"
      :actions="false"
      #default="{ state: { valid } }"
      validation-visibility="blur"
    >
      <FormKit
        name="oldPass"
        outer-class="mb-4"
        type="password"
        :label="$t('PasswordOldLabel')"
        validation="required"
      />

      <FormKit
        outer-class="mb-4"
        type="password"
        name="newPass"
        :label="$t('PasswordNewLabel')"
        validation="required|length:8|hasUppercase|hasLowercase|hasNumber|hasSpecialChar"
        :validation-rules="{
          hasUppercase,
          hasLowercase,
          hasNumber,
          hasSpecialChar,
        }"
        :validation-messages="{
          length: $t('PasswordLengthError', { length: 8 }),
          hasUppercase: $t('PasswordUppercaseError'),
          hasLowercase: $t('PasswordLowercaseError'),
          hasNumber: $t('PasswordNumberError'),
          hasSpecialChar: $t('PasswordSpecialCharError'),
        }"
      />

      <FormKit
        outer-class="mb-4"
        type="password"
        name="newPass_confirm"
        :label="$t('PasswordConfirmLabel')"
        validation="required|confirm"
        :validation-messages="{
          confirm: $t('Les mots de passe ne correspondent pas'),
        }"
      />

      <div>
        <klereo-captcha :refresh="refreshCaptcha" />
        <FormKit
          name="captcha"
          outer-class="mb-4"
          type="password"
          validation="required"
          :label="$t('EnterSecur')"
        />
      </div>

      <div class="flex justify-between mt-5">
        <div class="text-green-400">
          <span v-if="complete">{{ $t("PasswordChangeSuccess") }}</span>
        </div>
        <FormKit
          type="submit"
          input-class="btn--primary"
          :disabled="!valid"
          :label="$t('Valid')"
        />
      </div>
    </FormKit>
  </div>
</template>
